import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  Box,
  BoxProps,
  Dialog,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Link,
  Stack,
  StackProps,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';

import {
  Download as DownloadIcon,
  Send as SendIcon,
  HourglassBottom as HourglassBottomIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AddLink as AddLinkIcon,
  FilePresent as FilePresentIcon,
} from '@mui/icons-material';

import { TP_COLORS } from '../../../../../../constants';
import { ISection } from '../../../../../../store/V2/SectionSlice';
import { useAppSelector } from '../../../../../../hooks';
import { TPTextField } from '../../../../../../components/Inputs';

import BasicLoader from '../../../../../../components/Loaders/BasicLoader';
import {
  createHomeworkSubmissionAsAdmin,
  deleteHomeworkSubmissionAsAdmin,
  getHomeworkSubmissionsAsAdmin,
  TAdminCreateHomeworkSubmission,
  TAdminUpdateHomeworkSubmission,
  updateHomeworkSubmissionAsAdmin,
} from '../../../../../../store/V2/HomeworksSlice/adminApi';
import {
  getHomeworksState,
  IHomework,
  resetHomeworkSubmissions,
} from '../../../../../../store/V2/HomeworksSlice';
import AddFileModal from './AddFileModal';
import AddLinkModal from './AddLinkModal';
import { getAuthState } from '../../../../../../store/AuthSlice';
import SelectStudentModal from './SelectStudentModal';
import {
  getCourseStudentsAsAdmin,
  getCourseStudentsState,
  resetAdminCourseStudentsData,
} from '../../../../../../store/V2/CourseStudentsSlice';
import { IUser } from '../../../../../../store/UserSlice';
import { getFormattedTime } from '../../../../../../helper';

const stackProps: StackProps = {
  direction: 'row',
  alignItems: 'center',
  spacing: 2,
  maxWidth: '75%',
  sx: {
    backgroundColor: TP_COLORS.white,
    padding: '10px',
    borderRadius: '10px',
    border: '1px solid #E2E8F0',
  },
};

const bottomStackProps: StackProps = {
  alignItems: 'center',
  justifyContent: 'center',
  height: '150px',
};

interface HomeworkModalProps {
  open: boolean;
  onClose: () => void;
  section: ISection;
}

type TState = {
  description: string;
  isAddFileModalOpen: boolean;
  isAddLinkModalOpen: boolean;
  isSelectStudentModalOpen: boolean;
  selectedStudent: IUser | null;
  selectedHomework?: IHomework;
};

const initialState: TState = {
  description: '',
  isAddFileModalOpen: false,
  isAddLinkModalOpen: false,
  isSelectStudentModalOpen: false,
  selectedStudent: null,
  selectedHomework: undefined,
};

const ViewHomeworkModal: React.FC<HomeworkModalProps> = ({
  open,
  onClose,
  section,
}) => {
  const dispatch = useDispatch();

  // Store
  const { homeworkSubmissions, status: homeworkStatus } = useAppSelector(
    getHomeworksState
  );
  const { user: authUser } = useAppSelector(getAuthState);
  const { adminCourseStudentsData, status: studentStatus } = useAppSelector(
    getCourseStudentsState
  );

  // State
  const [state, setState] = useState(initialState);

  const isLoading = homeworkStatus === 'loading' || studentStatus === 'loading';

  useEffect(() => {
    return () => {
      setState(initialState);
      dispatch(resetHomeworkSubmissions());
    };
  }, []);

  useEffect(() => {
    if (
      adminCourseStudentsData &&
      adminCourseStudentsData.list &&
      adminCourseStudentsData.list.length
    ) {
      if (
        adminCourseStudentsData.list.length === 1 &&
        adminCourseStudentsData.list[0] &&
        adminCourseStudentsData.list[0].student
      ) {
        setState((prevState) => ({
          ...prevState,
          selectedStudent: adminCourseStudentsData.list[0].student,
          isSelectStudentModalOpen: true,
        }));
      } else if (adminCourseStudentsData.list.length > 1) {
        toggleStudentModalOpen();
      }
    }
  }, [adminCourseStudentsData]);

  useEffect(() => {
    if (state.selectedStudent && section) {
      dispatch(
        getHomeworkSubmissionsAsAdmin({
          section: section._id,
          student: state.selectedStudent._id,
        })
      );
    }
  }, [state.selectedStudent]);

  const handleSubmit = (values) => {
    if (state.selectedStudent) {
      if (state.selectedHomework && state.selectedHomework._id) {
        const apiData: TAdminUpdateHomeworkSubmission = {
          _id: state.selectedHomework._id,
          description: values.description,
        };

        if (values.files && values.files.length) {
          apiData.files = values.files;
        }

        if (values.link) {
          apiData.link = values.link;
        }

        dispatch(updateHomeworkSubmissionAsAdmin(apiData));
      } else {
        const apiData: TAdminCreateHomeworkSubmission = {
          student: state.selectedStudent._id,
          section: section._id,
          description: values.description,
        };

        if (values.files && values.files.length) {
          apiData.files = values.files;
        }

        if (values.link) {
          apiData.link = values.link;
        }

        dispatch(createHomeworkSubmissionAsAdmin(apiData));
      }
      setState((prevState) => ({
        ...prevState,
        description: '',
      }));
    }
  };

  const handleUpdate = (homework: IHomework) => {
    setState((prevState) => {
      const newState: TState = {
        ...prevState,
        selectedHomework: homework,
      };

      if (homework.files && homework.files.length) {
        newState.isAddFileModalOpen = true;
      } else if (homework.link) {
        newState.isAddLinkModalOpen = true;
      } else if (homework.description) {
        newState.description = homework.description;
      }

      return newState;
    });
  };

  const handleDelete = (values: IHomework) => {
    if (values._id) {
      dispatch(deleteHomeworkSubmissionAsAdmin(values._id));
    }
  };

  const toggleAddFileModalOpen = () => {
    setState((prevState) => ({
      ...prevState,
      selectedHomework: undefined,
      isAddFileModalOpen: !prevState.isAddFileModalOpen,
    }));
  };

  const toggleAddLinkModalOpen = () => {
    setState((prevState) => ({
      ...prevState,
      selectedHomework: undefined,
      isAddLinkModalOpen: !prevState.isAddLinkModalOpen,
    }));
  };

  const toggleStudentModalOpen = () => {
    setState((prevState) => ({
      ...prevState,
      isSelectStudentModalOpen: !prevState.isSelectStudentModalOpen,
    }));
  };

  const handleStudentSelect = (student: IUser) => {
    setState((prevState) => ({
      ...prevState,
      selectedStudent: student,
      isSelectStudentModalOpen: false,
    }));
  };

  const renderActions = (homeworkItem: IHomework) => (
    <Box>
      <Tooltip title="Edit">
        <IconButton onClick={() => handleUpdate(homeworkItem)} size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          color="error"
          onClick={() => handleDelete(homeworkItem)}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          padding: 0,
          maxWidth: '635px',
        },
      }}
    >
      {/* Header Section */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        padding="16px"
        paddingLeft="30px"
      >
        <Avatar alt="User" />
        <Box>
          <Typography
            fontSize="14px"
            fontWeight="600"
            marginLeft="2px"
            color="#212121"
          >
            {section.title}
          </Typography>
          {section.dueDate && (
            <Typography fontSize="12px" fontWeight="600" color="#3498DB">
              <HourglassBottomIcon
                sx={{ fontSize: 'small', verticalAlign: 'middle' }}
              />{' '}
              DUE {moment(section.dueDate).fromNow().toUpperCase()}
            </Typography>
          )}
        </Box>
      </Stack>

      <Divider />
      <Stack
        spacing="10px"
        sx={{
          maxHeight: '450px',
          overflowY: 'auto',
          padding: '27px 32px',
          background: `url('${process.env.PUBLIC_URL}/images/backgrounds/bg-homework.png') top left/cover`,
        }}
      >
        {homeworkSubmissions?.length
          ? homeworkSubmissions.map((submissionItem, index) => {
              const formattedDate = moment(submissionItem.createdAt).format(
                'ddd MMM DD YYYY, hh:mm:ss A'
              );
              const createdUser = submissionItem.createdBy;
              const isSender = authUser?._id === createdUser?._id;

              const boxProps: BoxProps = {
                display: 'flex',
                alignItems: 'center',
                justifyContent: isSender ? 'flex-end' : 'flex-start',
              };

              if (submissionItem.files && submissionItem.files.length) {
                return (
                  <Box key={index} {...boxProps}>
                    {isSender && (
                      <IconButton
                        href={submissionItem.files[0].url}
                        sx={{
                          backgroundColor: TP_COLORS.white,
                          marginRight: '20px',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}

                    <Stack {...stackProps}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/homework/icon-pdf.png`}
                        alt="icon-pdf"
                      />
                      <Box>
                        <Typography
                          fontSize="15px"
                          color={TP_COLORS.gray.dark}
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 1,
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {submissionItem.files[0].name}
                        </Typography>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Box>
                            <Typography fontSize="12px" color="#94A3B8">
                              {formattedDate}
                            </Typography>
                            {createdUser && (
                              <Typography fontSize="12px" color="#94A3B8">
                                By {createdUser.firstname}{' '}
                                {createdUser.lastname || ''}
                              </Typography>
                            )}
                          </Box>

                          {renderActions(submissionItem)}
                        </Stack>
                      </Box>
                    </Stack>
                    {!isSender && (
                      <IconButton
                        href={submissionItem.files[0].url}
                        sx={{
                          backgroundColor: TP_COLORS.white,
                          marginLeft: '20px',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </Box>
                );
              }

              if (submissionItem.quizzes && submissionItem.quizzes.length) {
                return (
                  <Box key={index} {...boxProps}>
                    <Stack key={index} {...stackProps}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/homework/icon-quiz.png`}
                        alt="icon-quiz"
                      />
                      <Box>
                        <Link
                          href={submissionItem.quizzes[0]._id}
                          target="_blank"
                          underline="hover"
                          sx={{
                            fontSize: '14px',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {submissionItem.quizzes[0]._id}
                        </Link>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Box>
                            <Typography fontSize="12px" color="#94A3B8">
                              {formattedDate}
                            </Typography>
                            {createdUser && (
                              <Typography fontSize="12px" color="#94A3B8">
                                By {createdUser.firstname}{' '}
                                {createdUser.lastname || ''}
                              </Typography>
                            )}
                          </Box>

                          {renderActions(submissionItem)}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              }

              if (submissionItem.link) {
                return (
                  <Box key={index} {...boxProps}>
                    <Stack key={index} {...stackProps}>
                      <Box>
                        <Link
                          href={submissionItem.link}
                          target="_blank"
                          underline="hover"
                          sx={{
                            fontSize: '14px',
                            marginBottom: '3px',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                            textOverflow: 'ellipsis',
                            maxWidth: '375px',
                          }}
                        >
                          {submissionItem.link}
                        </Link>

                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Box>
                            <Typography fontSize="12px" color="#94A3B8">
                              {formattedDate}
                            </Typography>
                            {createdUser && (
                              <Typography fontSize="12px" color="#94A3B8">
                                By {createdUser.firstname}{' '}
                                {createdUser.lastname || ''}
                              </Typography>
                            )}
                          </Box>

                          {renderActions(submissionItem)}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              }

              if (submissionItem.description) {
                return (
                  <Box key={index} {...boxProps}>
                    <Stack {...stackProps}>
                      <Box>
                        <Typography fontSize="16px" color={TP_COLORS.gray.dark}>
                          {submissionItem.description}
                        </Typography>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                        >
                          <Box>
                            <Typography fontSize="12px" color="#94A3B8">
                              {formattedDate}
                            </Typography>
                            {createdUser && (
                              <Typography fontSize="12px" color="#94A3B8">
                                By {createdUser.firstname}{' '}
                                {createdUser.lastname || ''}
                              </Typography>
                            )}
                          </Box>

                          {renderActions(submissionItem)}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              }

              if (submissionItem.totalScore && submissionItem.completedAt) {
                const submissionDate = moment(
                  submissionItem.completedAt
                ).format('YYYY-MM-DD hh:mm A');

                let timeSpent = '00: 00: 00';

                if (submissionItem.timerStart && submissionItem.timerEnd) {
                  const _spentSeconds = Math.floor(
                    (submissionItem.timerEnd - submissionItem.timerStart) / 1000
                  );
                  timeSpent = getFormattedTime(_spentSeconds);
                }

                return (
                  <Box key={index} {...boxProps}>
                    <Grid
                      container
                      sx={{
                        color: '#F8FAFC',
                        backgroundColor: TP_COLORS.blue.dark,
                        borderRadius: '10px',
                        padding: '10px 20px',
                        maxWidth: '492px',
                      }}
                    >
                      <Grid item xs={2}>
                        <Typography fontSize="12px">Attempt</Typography>
                        <Typography fontSize="16px">
                          {(submissionItem.attempt || 0)
                            .toString()
                            .padStart(2, '0')}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography fontSize="12px">
                          Submission Date & Time
                        </Typography>
                        <Typography fontSize="16px">
                          {submissionDate}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography fontSize="12px">Score</Typography>
                        <Typography fontSize="16px">
                          {submissionItem.score}/{submissionItem.totalScore}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography fontSize="12px">Time Taken</Typography>
                        <Typography fontSize="16px">{timeSpent}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                );
              }

              return null;
            })
          : null}
        {isLoading && !homeworkSubmissions?.length && (
          <Stack {...bottomStackProps}>
            <BasicLoader />
          </Stack>
        )}
        {!isLoading && homeworkSubmissions && !homeworkSubmissions.length ? (
          <Stack {...bottomStackProps}>
            <Typography fontSize="14px">No homework available</Typography>
          </Stack>
        ) : null}
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        padding="16px"
        paddingLeft="30px"
      >
        <IconButton onClick={toggleAddFileModalOpen}>
          <FilePresentIcon />
        </IconButton>
        <IconButton onClick={toggleAddLinkModalOpen}>
          <AddLinkIcon />
        </IconButton>
        <FormControl fullWidth>
          <TPTextField
            name="description"
            size="small"
            placeholder="Comments or questions"
            value={state.description}
            onChange={(event) =>
              setState((prevState) => ({
                ...prevState,
                description: event.target.value,
              }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' && state.description.trim().length) {
                handleSubmit(state);
              }
            }}
          />
        </FormControl>
        <IconButton
          onClick={() => handleSubmit(state)}
          disabled={!state.description.trim().length}
        >
          <SendIcon />
        </IconButton>
      </Stack>
      {/* Modals */}
      {state.isSelectStudentModalOpen && (
        <SelectStudentModal
          open={state.isSelectStudentModalOpen}
          onClose={toggleStudentModalOpen}
          onSelect={handleStudentSelect}
        />
      )}
      {state.isAddFileModalOpen ? (
        <AddFileModal
          open={state.isAddFileModalOpen}
          homework={state.selectedHomework}
          onClose={toggleAddFileModalOpen}
          onSubmit={handleSubmit}
        />
      ) : null}
      {state.isAddLinkModalOpen && (
        <AddLinkModal
          open={state.isAddLinkModalOpen}
          homework={state.selectedHomework}
          onClose={toggleAddLinkModalOpen}
          onSubmit={handleSubmit}
        />
      )}
    </Dialog>
  );
};

export default ViewHomeworkModal;
